import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import MainNavigation from '../../components/ui/MainNavigation';
import Icon from '../../components/AppIcon';
import MarketplaceMetrics from './components/MarketplaceMetrics';
import OwnerVerificationCenter from './components/OwnerVerificationCenter';
import DisputeTracking from './components/DisputeTracking';
import ContentModeration from './components/ContentModeration';

import FinancialOversight from './components/FinancialOversight';
import RealTimeActivityFeed from './components/RealTimeActivityFeed';
import ActivityLog from './components/ActivityLog';
import PlatformMetrics from '../admin-dashboard/components/PlatformMetrics';

import UserManagement from './components/UserManagement';
import Button from '../../components/ui/Button';


const AdminPlatformManagement = () => {
  const { user, userProfile, isAuthenticated, loading, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('metrics');
  const [authVerified, setAuthVerified] = useState(false);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
    // Redirect non-admin users to home page
    if (!loading && isAuthenticated && !isAdmin) {
      navigate('/home-page');
    }
    // Set auth verified after role confirmation
    if (!loading && isAuthenticated && isAdmin) {
      setAuthVerified(true);
    }
  }, [loading, isAuthenticated, isAdmin, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <span className="font-body text-muted-foreground">Verifying admin credentials...</span>
        </div>
      </div>
    );
  }

  // Access denied for non-admin users
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-20 h-20 bg-error/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Icon name="ShieldAlert" size={40} className="text-error" />
          </div>
          <h2 className="font-heading text-3xl font-bold text-foreground mb-3">Access Denied</h2>
          <p className="font-body text-muted-foreground mb-2">Administrator privileges required</p>
          <p className="font-body text-sm text-muted-foreground mb-8">You do not have permission to access the admin platform. This area is restricted to verified platform administrators only.</p>
          <Button onClick={() => navigate('/home-page')} className="w-full sm:w-auto">
            <Icon name="Home" size={18} className="mr-2" />
            Return to Home
          </Button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'metrics', label: 'Platform Metrics', icon: 'BarChart3' },
    { id: 'users', label: 'User Management', icon: 'Users' },
    { id: 'overview', label: 'Marketplace Overview', icon: 'LayoutDashboard' },
    { id: 'verification', label: 'Verification Center', icon: 'ShieldCheck' },
    { id: 'disputes', label: 'Dispute Tracker', icon: 'AlertTriangle' },
    { id: 'moderation', label: 'Content Moderation', icon: 'Flag' },
    { id: 'financial', label: 'Financial Oversight', icon: 'DollarSign' },
    { id: 'realtime', label: 'Live Activity', icon: 'Activity' },
    { id: 'logs', label: 'Activity Log', icon: 'FileText' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <MainNavigation isAuthenticated={isAuthenticated} user={user} />
      
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header with Authentication Confirmation */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <div className="flex items-start space-x-4">
                <div className="w-14 h-14 bg-gradient-to-br from-primary to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Icon name="Shield" size={28} strokeWidth={2.5} className="text-white" />
                </div>
                <div>
                  <h1 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-1">Admin Platform Management</h1>
                  <p className="font-body text-muted-foreground">Unified control center for comprehensive platform oversight</p>
                </div>
              </div>
              
              {/* Role Verification Badge */}
              {authVerified && (
                <div className="flex items-center space-x-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-lg">
                  <Icon name="CheckCircle" size={20} className="text-primary" />
                  <div className="text-left">
                    <p className="font-body text-xs text-muted-foreground">Authenticated as</p>
                    <p className="font-body font-semibold text-sm text-foreground">Administrator</p>
                  </div>
                </div>
              )}
            </div>

            {/* Admin Info Card */}
            <div className="bg-card rounded-lg shadow-elevation-2 p-4 border-l-4 border-primary">
              <div className="flex items-start space-x-3">
                <Icon name="Info" size={20} className="text-primary mt-0.5" />
                <div className="flex-1">
                  <p className="font-body text-sm text-foreground mb-1">
                    <span className="font-semibold">Welcome, {userProfile?.full_name || user?.email}</span>
                  </p>
                  <p className="font-body text-xs text-muted-foreground">
                    You have full administrative access to all platform management tools, user oversight, metrics, verification, disputes, moderation, financial controls, and system analytics.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="bg-card rounded-lg shadow-elevation-2 mb-6 overflow-x-auto">
            <div className="flex space-x-1 p-2 min-w-max">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center space-x-2 px-4 py-3 rounded-md font-body font-medium transition-smooth ${
                    activeTab === tab?.id
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} strokeWidth={2} />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {activeTab === 'metrics' && <PlatformMetrics />}
            {activeTab === 'users' && <UserManagement />}
            {activeTab === 'overview' && <MarketplaceMetrics />}
            {activeTab === 'verification' && <OwnerVerificationCenter />}
            {activeTab === 'disputes' && <DisputeTracking />}
            {activeTab === 'moderation' && <ContentModeration />}
            {activeTab === 'financial' && <FinancialOversight />}
            {activeTab === 'realtime' && <RealTimeActivityFeed />}
            {activeTab === 'logs' && <ActivityLog />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPlatformManagement;